import { useRef, useCallback, useEffect, useMemo, useState } from "react";
import { useTripOrTransportIndexes } from "src/utils/hooks/useTripOrTransportIndexes";
import { useIsHotelsUrlDeeplink } from "src/utils/hooks/useNavigateToHotelsPage";
import { sendAnalyticsNonInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import type { SlotConfigId } from "../DisplayAd/config";

type RefreshOnPaneChangeProps = {
  refreshThreshold: number;
  slotConfigId: SlotConfigId;
  maxRefreshCount?: number;
};

type AdStatus = "viewed" | "notFilled";

/**
 * A hook which manages ad refresh counts based on a minimum threshold
 * of time in milliseconds and the screen type in which the user has navigated.
 *
 * @param refreshThreshold (in milliseconds) - how long until a refresh can be triggered.
 * @param slotConfigId - SlotConfigId of the ad which will be refreshing.
 * @param maxRefreshCount (optional) - the maximum number of times an ad can be refreshed.
 * @returns {Object} an object with:
 *   - refreshCount {number} - the current number of times the ad has been refreshed.
 *   - handleViewChange() {(status: "viewed" | "notFilled") => void} - a function to call when an ad either fails to fill,
 *     or is viewed by the user, thus making it eligible for a refresh.
 *     Once triggered it will increment the refresh count, log an analytics event and reset the timer for the next iteration.
 */

export function useRefreshOnPaneChange({
  refreshThreshold,
  slotConfigId,
  maxRefreshCount = Infinity,
}: RefreshOnPaneChangeProps) {
  const adViewedTimestamp = useRef<number | undefined>(undefined);
  const adSlotStatus = useRef<AdStatus | undefined>(undefined);
  const { routeIndex, routeSegmentIndex } = useTripOrTransportIndexes();
  const isHotelScreen = useIsHotelsUrlDeeplink();
  const [refreshCount, setRefreshCount] = useState(0);

  const incrementRefreshCount = useCallback(
    (count: number) => {
      const newRefreshCount = count + 1;

      sendAnalyticsNonInteractionEvent({
        category: "Ads",
        action: "Refresh",
        label: slotConfigId,
        value: newRefreshCount,
      });

      return newRefreshCount;
    },
    [slotConfigId],
  );

  const refreshAd = useCallback(() => {
    setRefreshCount(incrementRefreshCount);

    // Reset back to initial state so that we are
    // ready for the next refresh.
    adViewedTimestamp.current = undefined;
    adSlotStatus.current = undefined;
  }, [incrementRefreshCount]);

  const canRefresh = refreshCount < maxRefreshCount;

  const isRefreshablePage = useMemo(() => {
    const isSegmentPage =
      routeIndex !== undefined && routeSegmentIndex !== undefined;

    return isSegmentPage || isHotelScreen;
  }, [routeIndex, routeSegmentIndex, isHotelScreen]);

  useEffect(() => {
    const hasAchievedThreshold =
      adViewedTimestamp.current !== undefined &&
      adViewedTimestamp.current + refreshThreshold <= Date.now();
    const shouldRefresh =
      adSlotStatus.current === "notFilled" ||
      (adSlotStatus.current === "viewed" &&
        isRefreshablePage &&
        hasAchievedThreshold);

    if (shouldRefresh) {
      refreshAd();
    }
  }, [
    slotConfigId,
    isRefreshablePage,
    refreshThreshold,
    setRefreshCount,
    incrementRefreshCount,
    refreshAd,
  ]);

  const handleViewChange = useCallback(
    (status: AdStatus) => {
      if (status === "viewed" && canRefresh) {
        adViewedTimestamp.current = Date.now();
      }

      adSlotStatus.current = status;
    },
    [canRefresh],
  );

  const forceRefresh = useCallback(() => {
    refreshAd();
  }, [refreshAd]);

  return {
    refreshCount,
    handleViewChange,
    forceRefresh,
  };
}
